import { useFormContext } from 'react-hook-form';

import { useApplicationNavigation } from '@/components/modules/Application/hooks/useApplicationNavigation';
import { useSetIsDirtyListener } from '@/components/modules/Application/hooks/useSetDirtyListener';
import { IberiaCardsNav } from '@/components/modules/Application/IberiaCards/_shared/components/IberiaCardsNav';
import { SalePointAndProductFields } from '@/components/modules/Application/IberiaCards/modules/SalePointAndProduct/components/SalePointAndProductFields';
import { useLastSalePoint } from '@/components/modules/Application/IberiaCards/modules/SalePointAndProduct/hooks/useLastSalePoint';
import { useSalePointAndProduct } from '@/components/modules/Application/IberiaCards/modules/SalePointAndProduct/hooks/useSalePointAndProduct';
import { useStandsAgent } from '@/components/modules/Application/IberiaCards/modules/SalePointAndProduct/hooks/useStandsAgent';
import { SalePointAndProductSchema } from '@/components/modules/Application/IberiaCards/modules/SalePointAndProduct/schemas/SalePointAndProduct.schema';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { FormWrapper } from '@/components/shared/components/FormWrapper';
import { Text } from '@/components/shared/components/Text';
import { useApplicationContext } from '@/contexts/ApplicationContext/ApplicationContext';

export const SalePointAndProduct = () => {
  const {
    state: { iberiaCardsStandsSelection },
  } = useApplicationContext();
  const { standsAgent } = useStandsAgent();
  const { lastSalePointId } = useLastSalePoint();

  if (!standsAgent) {
    return (
      <CustomAlert
        severity="warning"
        title={'IBERIA_CARDS_STANDS_AGENT_NOT_CONFIGURED'}
        description={'IBERIA_CARDS_STANDS_AGENT_NOT_CONFIGURED_DESCRIPTION'}
      />
    );
  }

  return (
    <FormWrapper
      formSchema={SalePointAndProductSchema}
      defaultValues={{
        salePoint: iberiaCardsStandsSelection?.salePoint.id ?? lastSalePointId ?? '',
        campaign: iberiaCardsStandsSelection?.campaign.id ?? '',
      }}
    >
      <SalePointAndProductForm />
    </FormWrapper>
  );
};

const SalePointAndProductForm = () => {
  const { onPrev } = useApplicationNavigation();
  const { formState } = useFormContext();
  const { onSubmit } = useSalePointAndProduct();

  // Marks the application as started once the agent picks something, so ApplicationModule's PageLeaveGuard
  // (isDirty && !isCompleted) asks for confirmation before navigating away and losing the data of both steps.
  // Without it isDirty is never set in the stands flow and leaving it discards the application silently.
  // Compared against the default values, so the sale point prefilled from the last pick does not count as a change.
  useSetIsDirtyListener(formState.isDirty);

  return (
    <FlexColumn gridGap={32}>
      <Font variant="body1" color="textPrimary">
        <Text tId="IBERIA_CARDS_STANDS_SALE_POINT_AND_PRODUCT_INTRO" />
      </Font>
      <CustomPaper fill="paper">
        <SalePointAndProductFields />
      </CustomPaper>
      <IberiaCardsNav onNext={onSubmit} onPrev={onPrev} disableNext={!formState.isValid} />
    </FlexColumn>
  );
};
